import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { academicSessionAPI, feeCategoryAPI, feePaymentAPI, studentAPI } from "../utils/api";
import useDebouncedValue from "../hooks/useDebouncedValue";
import "../styles/form.css";
import "../styles/fee-payment.css";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const getGenderIconClass = (gender) => {
  if (gender === "Male") return "fas fa-mars";
  if (gender === "Female") return "fas fa-venus";
  return "fas fa-user";
};

const FeePayment = ({ isEditMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State management
  const [formData, setFormData] = useState({
    student_id: "",
    fee_category_id: "",
    academic_session_id: "",
    amount: "",
    remark: "",
    payment_date: new Date().toISOString().split("T")[0],
  });

  const [feeCategoryOptions, setFeeCategoryOptions] = useState([]);
  const [academicSessionOptions, setAcademicSessionOptions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    fetchFeeCategories();
    fetchAcademicSessions();
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && id) {
      fetchPayment();
    }
  }, [id, isEditMode]);

  const fetchFeeCategories = async () => {
    try {
      const response = await feeCategoryAPI.getAll({
        includeDisabled: isEditMode ? 1 : undefined,
      });
      const categories = response.data.data.feeCategories || [];
      setFeeCategoryOptions(categories);

      if (!isEditMode && !formData.fee_category_id) {
        const defaultCategory = categories.find(
          (category) => Number(category.isDefault) === 1
        );

        if (defaultCategory) {
          setFormData((prev) => ({
            ...prev,
            fee_category_id: String(defaultCategory.id),
          }));
        }
      }
    } catch (error) {
      setMessage(
        `Error: ${error.response?.data?.message || "Failed to fetch fee categories"}`
      );
    }
  };

  const fetchAcademicSessions = async () => {
    try {
      const response = await academicSessionAPI.getAll({
        includeDisabled: isEditMode ? 1 : undefined,
      });
      const sessions = response.data.data.academicSessions || [];
      setAcademicSessionOptions(sessions);

      if (!isEditMode && !formData.academic_session_id) {
        const defaultSession = sessions.find(
          (session) => Number(session.isDefault) === 1
        );

        if (defaultSession) {
          setFormData((prev) => ({
            ...prev,
            academic_session_id: String(defaultSession.id),
          }));
        }
      }
    } catch (error) {
      setMessage(
        `Error: ${error.response?.data?.message || "Failed to fetch academic sessions"}`
      );
    }
  };

  // Fetch payment for edit mode
  const fetchPayment = async () => {
    try {
      const response = await feePaymentAPI.getById(id);
      const payment = response.data.data.payment;

      setFormData({
        student_id: payment.student_id,
        fee_category_id: payment.fee_category_id,
        academic_session_id: payment.academic_session_id,
        amount: payment.amount,
        remark: payment.remark || "",
        payment_date: payment.payment_date,
      });

      setSearchQuery(payment.student_name);

      // Set selected student with complete data from API
      setSelectedStudent({
        id: payment.student_id,
        name: payment.student_name,
        gender: payment.gender,
        class: payment.class,
        aadharNumber: payment.aadharNumber,
        fatherName: payment.fatherName,
      });
    } catch (error) {
      setMessage(
        `Error: ${error.response?.data?.message || "Failed to fetch payment"}`
      );
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  useEffect(() => {
    const trimmedQuery = debouncedSearchQuery.trim();

    if (!trimmedQuery) {
      setSearchResults([]);
      setShowDropdown(false);
      setSearchLoading(false);
      return;
    }

    if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setShowDropdown(true);
      setSearchLoading(false);
      return;
    }

    let isActive = true;

    const searchStudents = async () => {
      setShowDropdown(true);
      setSearchLoading(true);

      try {
        const response = await studentAPI.getAll({
          search: trimmedQuery,
          limit: 10,
        });
        if (isActive) {
          setSearchResults(response.data.data.students || []);
        }
      } catch (error) {
        console.error("Error searching students:", error);
        if (isActive) {
          setSearchResults([]);
        }
      } finally {
        if (isActive) {
          setSearchLoading(false);
        }
      }
    };

    searchStudents();

    return () => {
      isActive = false;
    };
  }, [debouncedSearchQuery]);

  // Handle student selection from search results
  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setSearchQuery(student.name);
    setFormData((prev) => ({
      ...prev,
      student_id: student.id,
    }));
    setShowDropdown(false);
    setSearchResults([]);
    setErrors((prev) => ({
      ...prev,
      student_id: "",
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.student_id || formData.student_id === "") {
      newErrors.student_id = "Please select a student";
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      newErrors.amount = "Amount must be a whole number greater than 0";
    } else if (!Number.isInteger(Number(formData.amount))) {
      newErrors.amount = "Amount must be a whole number";
    }

    if (!formData.fee_category_id || formData.fee_category_id === "") {
      newErrors.fee_category_id = "Please select a fee category";
    }

    if (!formData.academic_session_id || formData.academic_session_id === "") {
      newErrors.academic_session_id = "Please select an academic session";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage("");
    setSuccessMessage("");

    try {
      const submitData = {
        ...formData,
        student_id: parseInt(formData.student_id),
        fee_category_id: parseInt(formData.fee_category_id),
        academic_session_id: parseInt(formData.academic_session_id),
        amount: parseInt(formData.amount, 10),
      };

      if (isEditMode) {
        await feePaymentAPI.update(id, submitData);
        setSuccessMessage("Payment updated successfully!");
      } else {
        await feePaymentAPI.create(submitData);
        setSuccessMessage("Payment recorded successfully!");
      }

      setTimeout(() => {
        navigate("/fee-details");
      }, 1500);
    } catch (error) {
      setMessage(
        `Error: ${error.response?.data?.message || "Something went wrong"}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="form-page fee-payment-page">
        <div className="card">
          <div className="form-header">
            <h2>
              <i className="fas fa-wallet"></i>
              {isEditMode ? "Edit Payment" : "Record New Payment"}
            </h2>
            <p>
              {isEditMode
                ? "Update fee payment details"
                : "Record a new fee payment for a student"}
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="alert alert-success mb-3 fee-payment-alert">
              <i className="fas fa-check-circle"></i>
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {message && (
            <div className="alert alert-error mb-3 fee-payment-alert">
              <i className="fas fa-exclamation-circle"></i>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Section Header */}
            <h3>
              <i className="fas fa-user-graduate"></i> Student & Payment
              Information
            </h3>

            <div>
              {/* Student Search */}
              <div className="form-group">
                <label>
                  Select Student
                  <span className="required">*</span>
                </label>
                <div className="student-search-wrapper">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => searchQuery.trim().length >= MIN_SEARCH_LENGTH && setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    placeholder="Type at least 3 characters to search student by name..."
                    className="student-search-input"
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="btn-clear-search"
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults([]);
                        setShowDropdown(false);
                        setSelectedStudent(null);
                        setFormData(prev => ({ ...prev, student_id: '' }));
                      }}
                      title="Clear search"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}

                  {/* Search Results Dropdown */}
                  {showDropdown && searchQuery && (
                    <div className="student-search-dropdown" onMouseDown={(e) => e.preventDefault()}>
                      {searchQuery.trim().length < MIN_SEARCH_LENGTH ? (
                        <div className="student-search-empty">
                          Type at least 3 characters to search
                        </div>
                      ) : searchLoading ? (
                        <div className="student-search-loading">
                          <i className="fas fa-spinner fa-spin"></i> Searching...
                        </div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((student) => (
                          <div
                            key={student.id}
                            className="student-search-item"
                            onMouseDown={() => handleSelectStudent(student)}
                          >
                            <div className="student-name name-with-gender">
                              <i
                                className={`${getGenderIconClass(student.gender)} gender-icon gender-icon-${(student.gender || "").toLowerCase()}`}
                                aria-hidden="true"
                              ></i>
                              <span>{student.name}</span>
                            </div>
                            <div className="student-meta">
                              <span className="class-badge">Class {student.class}</span>
                              <span className="aadhar-badge">{student.aadharNumber}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="student-search-empty">
                          No students found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.student_id && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.student_id}
                  </div>
                )}
              </div>
              {/* Selected Student Details */}
              {selectedStudent && (
                <div className="selected-student-info">
                  <div className="info-badge">
                    <i className="fas fa-info-circle"></i>
                    <span className="badge-label">Selected Student:</span>
                    <span className="badge-value name-with-gender">
                      <i
                        className={`${getGenderIconClass(selectedStudent.gender)} gender-icon gender-icon-${(selectedStudent.gender || "").toLowerCase()}`}
                        aria-hidden="true"
                      ></i>
                      <span>{selectedStudent.name}</span>
                    </span>
                  </div>
                  <div className="info-row">
                    <div className="info-col">
                      <label>Class</label>
                      <p>{selectedStudent.class}</p>
                    </div>
                    <div className="info-col">
                      <label>Father's Name</label>
                      <p>{selectedStudent.fatherName}</p>
                    </div>
                    <div className="info-col">
                      <label>Aadhar Number</label>
                      <p>{selectedStudent.aadharNumber}</p>
                    </div>
                  </div>
                </div>
              )}
                
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Academic Session
                  <span className="required">*</span>
                </label>
                <select
                  name="academic_session_id"
                  value={formData.academic_session_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select academic session</option>
                  {academicSessionOptions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.name}
                      {Number(session.isDefault) === 1 ? " (Default)" : ""}
                      {Number(session.isEnabled) === 0 ? " (Disabled)" : ""}
                    </option>
                  ))}
                </select>
                {errors.academic_session_id && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.academic_session_id}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  Fee Category
                  <span className="required">*</span>
                </label>
                <select
                  name="fee_category_id"
                  value={formData.fee_category_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select fee category</option>
                  {feeCategoryOptions.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                      {Number(category.isEnabled) === 0 ? " (Disabled)" : ""}
                    </option>
                  ))}
                </select>
                {errors.fee_category_id && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.fee_category_id}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Amount (Rs)
                  <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  step="1"
                  min="1"
                  max="200000"
                  disabled={loading}
                />
                {errors.amount && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.amount}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
                <label>Remark / Notes</label>
                <textarea
                  name="remark"
                  value={formData.remark}
                  onChange={handleChange}
                  placeholder="Enter any remark about this payment (optional)"
                  rows="4"
                  disabled={loading}
                />
              </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                <i className="fas fa-save"></i>
                {loading ? "Saving..." : isEditMode ? "Update Payment" : "Save Payment"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/fee-details")}
                disabled={loading}
              >
                <i className="fas fa-times"></i>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

    </Layout>
  );
};

export default FeePayment;
